import React from 'react';
import { StarRating, BettrScore } from './HelperComponents';

const ProductCard = ({ product, isSelected, onToggleSelect, onViewDetails }) => {
    const getRatingColor = (score) => {
        if (score >= 80) return 'excellent';
        if (score >= 70) return 'good';
        if (score >= 60) return 'fair';
        if (score >= 50) return 'poor';
        return 'very-poor';
    };

    const formatProductName = (name) => {
        // Show product name only until the first comma or special character
        const specialChars = [',', '-', '|', ':', ';'];
        let truncatedName = name;

        for (const char of specialChars) {
            const index = name.indexOf(char);
            if (index > 0) {
                truncatedName = name.substring(0, index).trim();
                break;
            }
        }

        // Limit to 50 characters and add ellipsis if needed
        return truncatedName.length > 50 ? truncatedName.substring(0, 50) + '...' : truncatedName;
    };

    const getNutritionalHighlights = (product) => {
        const highlights = [];

        // High protein (> 15g per 100g/ml)
        if (product.protein_g > 15) highlights.push('High Protein');

        // Low sugar (< 5g per 100g/ml)
        if (product.total_sugars_g < 5) highlights.push('Low Sugar');

        // High fiber (> 6g per 100g/ml)
        if (product.fibre_g > 6) highlights.push('High Fiber');

        // Low sodium (< 120mg per 100g/ml)
        if (product.sodium_mg < 120) highlights.push('Low Sodium');

        // High fruit/veg content (> 50%)
        if (product.fruit_veg_nlm_percent > 50) highlights.push('Fruit/Veg Rich');

        return highlights.slice(0, 2); // Limit to 2 highlights
    };

    const getAdditivesCount = (product) => {
        if (!product.ins_e_ingredients_list) return 0;

        if (Array.isArray(product.ins_e_ingredients_list)) {
            return product.ins_e_ingredients_list.length;
        } else if (typeof product.ins_e_ingredients_list === 'string' && product.ins_e_ingredients_list.trim() !== '') {
            const dictStrings = product.ins_e_ingredients_list.split('; ').filter(s => s.trim());
            return dictStrings.length;
        }
        return 0;
    };

    const category = product.category_json?.["sub-category 3"];
    const highlights = getNutritionalHighlights(product);
    const additivesCount = getAdditivesCount(product);

    // Get key ingredients (up to 3)
    const getKeyIngredients = (product) => {
        if (!product.ingredient_list) return [];

        let ingredients = [];
        if (Array.isArray(product.ingredient_list)) {
            ingredients = product.ingredient_list;
        } else if (typeof product.ingredient_list === 'string') {
            try {
                const parsed = JSON.parse(product.ingredient_list);
                if (Array.isArray(parsed)) {
                    ingredients = parsed;
                } else {
                    ingredients = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                }
            } catch {
                ingredients = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
            }
        }

        // Return first 3 ingredients
        return ingredients.slice(0, 3);
    };

    const keyIngredients = getKeyIngredients(product);
    const hasMoreIngredients = keyIngredients.length < (Array.isArray(product.ingredient_list) ? product.ingredient_list.length : product.ingredient_list?.split(';').filter(Boolean).length || 0);

    // Determine market position based on price
    const getMarketPosition = (price) => {
        if (!price) return "Standard";
        if (price >= 800) return "Premium";
        if (price >= 500) return "Mid-range";
        return "Budget";
    };

    // Determine target audience based on product focus (from backend logic)
    const getTargetAudience = (product) => {
        const claims = product.benefit_claims?.toLowerCase() || '';
        const ingredients = product.ingredient_list?.toLowerCase() || '';

        if (claims.includes('sleep') || claims.includes('stress') || claims.includes('calm') ||
            ingredients.includes('ashwagandha') || ingredients.includes('melatonin')) {
            return "Sleep & Stress";
        }
        if (claims.includes('energy') || claims.includes('vitality') ||
            ingredients.includes('ginseng') || ingredients.includes('caffeine')) {
            return "Energy & Vitality";
        }
        if (claims.includes('hair') || claims.includes('skin') || claims.includes('beauty') ||
            ingredients.includes('biotin') || ingredients.includes('collagen')) {
            return "Hair, Skin & Nails";
        }
        if (claims.includes('kids') || claims.includes('children')) {
            return "Kids Wellness";
        }
        if (claims.includes('immunity') || claims.includes('immune')) {
            return "Immunity";
        }

        return "General Wellness";
    };

    const marketPosition = getMarketPosition(product.price);
    const targetAudience = getTargetAudience(product);

    return (
        <div
            className={`premium-product-card ${isSelected ? 'selected' : ''}`}
            // onClick={() => onToggleSelect(product)}
        >
            {/* Selection Checkbox */}
            <div className="premium-card-header">
                <div className="premium-selection-section">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(product)}
                        // onClick={() => onToggleSelect(product)}
                        className="premium-checkbox"
                    />
                    {category && (
                        <span className="premium-category-badge">{category}</span>
                    )}
                </div>
                <span className={`premium-position-badge ${
                    marketPosition === "Premium"
                        ? "premium-badge"
                        : marketPosition === "Mid-range"
                            ? "mid-badge"
                            : "budget-badge"
                }`}>
                    {marketPosition}
                </span>
            </div>

            {/* Product Image */}
            <div className="premium-image-section">
                <img
                    src={product.image_urls[0]}
                    alt={product.product_name}
                    className="premium-product-image"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src='https://via.placeholder.com/200x200/f0f0f0/666666?text=No+Image';
                    }}
                />
                <div className="premium-brand-overlay">{product.extracted_brand_name}</div>
                {additivesCount > 0 && (
                    <div className="premium-additives-indicator">
                        🧪 {additivesCount}
                    </div>
                )}
            </div>

            {/* Product Details */}
            <div className="premium-content">
                <h3 className="premium-product-title" title={product.product_name}>
                    {formatProductName(product.product_name)}
                </h3>

                <p className="premium-brand-name">{product.extracted_manufacturer_name || product.extracted_brand_name}</p>

                {/* Price and Rating Row */}
                <div className="premium-price-rating-row">
                    <div className="premium-price">₹{product.price || 'N/A'}</div>
                    <div className="premium-rating">
                        {product.average_rating > 0 && (
                            <>
                                <StarRating rating={product.average_rating} />
                                <span className="premium-rating-text">
                                    {product.average_rating} ({product.total_ratings || 0})
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Key Ingredients */}
                {keyIngredients.length > 0 && (
                    <div className="premium-ingredients-section">
                        <p className="premium-section-label">Key Ingredients:</p>
                        <div
                            className="premium-ingredients-list"
                            title={Array.isArray(product.ingredient_list)
                                ? product.ingredient_list.join(', ')
                                : product.ingredient_list?.split(';').filter(Boolean).join(', ') || ''
                            }
                        >
                            {keyIngredients.map((ingredient, index) => (
                                <span key={index} className="premium-ingredient-tag">
                                    {ingredient}
                                </span>
                            ))}
                            {hasMoreIngredients && (
                                <span className="premium-more-tag">
                                    +{Math.max(0, (Array.isArray(product.ingredient_list) ? product.ingredient_list.length : product.ingredient_list?.split(';').filter(Boolean).length || 0) - 3)} more
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Target Audience */}
                <div className="premium-target-section">
                    <p className="premium-section-label">Target:</p>
                    <span className="premium-target-badge">
                        {targetAudience}
                    </span>
                </div>

                {/* Action Buttons */}
                <div className="premium-actions">
                    <button
                        className="premium-view-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewDetails(product);
                        }}
                    >
                        👁️ View Details
                    </button>
                    <button
                        className={`premium-compare-btn ${isSelected ? 'selected' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect(product);
                        }}
                    >
                        {isSelected ? '✓ Selected' : '+'}
                        {/* {isSelected ? '✓' : '+'} */}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProductList = ({ products, selectedProducts, onToggleSelect, onViewDetails, totalCount, filters, setFilters }) => {
    // Debug: Log the actual products being rendered
    console.log('ProductList rendering with products:', products.map(p => ({ id: p.product_id, brand: p.extracted_brand_name })));

    const hasActiveFilters = filters.brand || filters.category || filters.manufacturer || filters.ingredient || (filters.specialFilters && filters.specialFilters.length > 0) || Object.keys(filters.nutritionalFilters || {}).length > 0;

    const clearAllFilters = () => {
        setFilters({
            brand: '',
            category: '',
            manufacturer: '',
            ingredients: [],
            search: '',
            specialFilters: [],
            nutritionalFilters: {}
        });
    };

    return (
        <div className="products-section">
            <div className="products-header">
                <div className="results-info">
                    <h2>Product Analysis Results</h2>
                    <div className="results-meta">
                        <span className="results-count">
                            Showing <strong>{products.length}</strong> of <strong>{totalCount}</strong> products
                        </span>
                        {hasActiveFilters && (
                            <button className="clear-filters-btn" onClick={clearAllFilters}>
                                Clear All Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {products.length > 0 ? (
                <div className="products-grid" key={`products-${products.length}`}>
                    {products.map((product, index) => (
                        <ProductCard
                            key={`${product.product_id}-${index}`}
                            product={product}
                            isSelected={selectedProducts.some(p => p.product_id === product.product_id)}
                            onToggleSelect={onToggleSelect}
                            onViewDetails={onViewDetails}
                        />
                    ))}
                </div>
            ) : (
                <div className="no-results">
                    <div className="no-results-icon">🔍</div>
                    <h3>No products match your criteria</h3>
                    <p>Try adjusting your filters to discover more products</p>
                    {hasActiveFilters && (
                        <button className="btn clear-filters-btn" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export { ProductCard, ProductList };
